import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { MetaChip } from "../../../../components/UIToolkit/Badges/MetaChip";
import { PersonActionRow } from "../../../../components/UIToolkit/Cells/PersonActionRow";
import ColorfulRole from "../../../../components/UIToolkit/ColorfulRole/ColorfulRole";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { useRosterNurse } from "../../../../hooks/useNurses";
import { getEmploymentTypeDisplayName } from "../../../../utils/utilFunctions";
import type { HiringStaffedNurse } from "../types";

type Props = {
	nurse: HiringStaffedNurse;
	/** Position in the list — entrance stagger only. Omit for no stagger. */
	index?: number;
};

/**
 * One nurse already scheduled on a slot: who they are, what their week looks like, and whether they're
 * carrying overtime. READ-ONLY — it had a "ask them to drop the shift" action for the Reduce board, and
 * that board is gone.
 *
 * Built on the generic `PersonActionRow` rather than its own flexbox: the row previously hand-rolled a
 * grey avatar circle, a bold name, an 11px meta line and a blue `ButtonBase` in hardcoded hex. The OT
 * flag is now a `MetaChip`, so it re-tints with the theme.
 */
const StaffedNurseRow: FC<Props> = ({ nurse, index }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const formatPersonName = useFormatPersonName();
	const fullNurse = useRosterNurse(chosenDepartment.department_id, nurse.nurseId);

	return (
		<PersonActionRow
			// DEFAULT: a nurse missing from the roster shows no name rather than a broken one.
			name={fullNurse ? formatPersonName(fullNurse) : ""}
			// Overtime is the signal that makes this row the one to look at, so it wears the accent wash.
			tone={nurse.isOvertime ? "accent" : "neutral"}
			index={index}
			badges={
				<>
					{fullNurse?.role && <ColorfulRole role={fullNurse.role} />}
					{nurse.isOvertime && <MetaChip label={t("hiring.staffed.otBadge")} tone="warning" dataTestId={`hiring-staffed-ot-chip-${nurse.nurseId}`} />}
				</>
			}
			meta={t("hiring.staffed.rowSubtitle", {
				employment: fullNurse?.employmentType ? getEmploymentTypeDisplayName(fullNurse.employmentType) : "—",
				hours: nurse.weeklyHours,
			})}
			dataTestId={`hiring-staffed-row-${nurse.nurseId}`}
		/>
	);
};

export default StaffedNurseRow;
